import { useState } from 'react';
import { Button, Popover, Select } from 'antd';
import { HexColorPicker } from 'react-colorful';
import chroma from 'chroma-js';
import { getArcGISLegendEntries } from './utils';
import { formatTitle } from '../util/TitleHelper';

// Palettes for recoloring a whole legend at once, so two layers with similar
// legends (e.g. SCIRP) can be told apart when overlaid.
const PALETTES = [
  'Blues',
  'Greens',
  'Oranges',
  'Purples',
  'Reds',
  'Greys',
  'Viridis',
  'Set1',
  'Dark2',
  'Paired',
];
const QUALITATIVE = ['Set1', 'Dark2', 'Paired'];

const paletteColors = (name, count) => {
  if (QUALITATIVE.includes(name)) {
    const list = chroma.brewer[name];
    return Array.from({ length: count }, (_, i) => list[i % list.length]);
  }
  // Padding skips the near-white start of the sequential ramps, which
  // disappears against the basemap.
  return chroma.scale(name).padding([0.25, 0]).mode('lch').colors(count);
};

const SWATCH_SIZE = 14;

const ColorSwatch = ({ entry, color, showImage, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(color);

  return (
    <Popover
      trigger="click"
      title="Select color"
      open={open}
      content={
        <div>
          <HexColorPicker color={draft} onChange={setDraft} />
          <div
            style={{
              marginTop: '4px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              className="btn_primary"
              onClick={() => {
                onApply(draft);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      }
    >
      <button
        type="button"
        onClick={() => {
          if (!open) setDraft(color);
          setOpen(!open);
        }}
        aria-label={`Change color for ${formatTitle(entry.label ?? '')}`}
        title="Change color"
        style={{
          display: 'inline-block',
          width: `${SWATCH_SIZE}px`,
          height: `${SWATCH_SIZE}px`,
          padding: 0,
          flex: 'none',
          cursor: 'pointer',
          backgroundColor: showImage ? 'transparent' : color,
          border: showImage ? 'none' : '1px solid #666',
        }}
      >
        {showImage && (
          <img
            src={entry.image}
            alt=""
            width={SWATCH_SIZE}
            height={SWATCH_SIZE}
            style={{ display: 'block' }}
          />
        )}
      </button>
    </Popover>
  );
};

const ArcGISLegendColors = (props) => {
  const { colorMap, overrides = {}, onChange } = props;
  const entries = getArcGISLegendEntries(colorMap);
  const hasOverrides = Object.keys(overrides).length > 0;

  const applyPalette = (name) => {
    const colors = paletteColors(name, entries.length);
    const next = {};
    entries.forEach((entry, i) => {
      next[entry.key] = colors[i];
    });
    onChange(next);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          margin: '2px 0 6px',
        }}
      >
        <Select
          size="small"
          placeholder="Recolor with palette"
          aria-label="Recolor legend with palette"
          value={null}
          style={{ width: '170px' }}
          onChange={applyPalette}
          options={PALETTES.map((name) => ({
            value: name,
            label: (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-block',
                    width: '40px',
                    height: '10px',
                    background: `linear-gradient(to right, ${paletteColors(name, 5).join(', ')})`,
                  }}
                />
                {name}
              </span>
            ),
          }))}
        />
        {hasOverrides && (
          <Button size="small" type="link" onClick={() => onChange({})}>
            Reset
          </Button>
        )}
      </div>
      {entries.map((entry) => {
        const override = overrides[entry.key];
        return (
          <div
            key={entry.key}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <ColorSwatch
              entry={entry}
              color={override ?? entry.color ?? '#000000'}
              showImage={!override && !!entry.image}
              onApply={(color) =>
                onChange({ ...overrides, [entry.key]: color })
              }
            />
            <span>{formatTitle(entry.label ?? '')}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ArcGISLegendColors;
